import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'
import {
  leaderboardOrderByOptions,
  type LeaderboardOrderBy,
  sacrificeOrderByOptions,
  type SacrificeOrderBy,
} from 'sacrifice-utils/api'

export type SortDirection = 'asc' | 'desc'

/**
 * Stored default sort per table. Page is intentionally NOT stored here — it
 * resets to 1 on each visit; only the column/direction persists.
 */
export interface TableSortPreference {
  leaderboard: { orderBy: LeaderboardOrderBy; orderDirection: SortDirection }
  sacrifices: { orderBy: SacrificeOrderBy; orderDirection: SortDirection }
}

/** App-wide cosmetic preferences, persisted client-side. */
export interface Preferences {
  /** Skip loading logo/banner images everywhere; render the LogoDisplay letter fallback instead. */
  disableImages: boolean
  /** Reveal campaigns tagged `test` in the directory. Cosmetic only — the API always returns them. */
  showTestCampaigns: boolean
  /** Per-table default sort column and direction. Page is not stored. */
  tableSort: TableSortPreference
}

export const DEFAULT_PREFERENCES: Preferences = {
  disableImages: false,
  showTestCampaigns: false,
  tableSort: {
    leaderboard: { orderBy: 'volume', orderDirection: 'desc' },
    sacrifices: { orderBy: 'timestamp', orderDirection: 'desc' },
  },
}

/** Single namespaced localStorage key holding the whole serialized Preferences object. */
export const PREFERENCES_STORAGE_KEY = 'sacrifice.preferences'

const isDirection = (value: unknown): value is SortDirection => value === 'asc' || value === 'desc'

/** Pure validator — returns DEFAULT_PREFERENCES.tableSort when input is absent or invalid. */
const parseTableSort = (value: unknown): TableSortPreference => {
  if (typeof value !== 'object' || value === null) return DEFAULT_PREFERENCES.tableSort
  const candidate = value as Record<string, { orderBy?: unknown; orderDirection?: unknown }>
  const lb = candidate.leaderboard
  const sac = candidate.sacrifices
  const leaderboardValid =
    !!lb &&
    (leaderboardOrderByOptions as readonly string[]).includes(lb.orderBy as string) &&
    isDirection(lb.orderDirection)
  const sacrificesValid =
    !!sac &&
    (sacrificeOrderByOptions as readonly string[]).includes(sac.orderBy as string) &&
    isDirection(sac.orderDirection)
  if (!leaderboardValid || !sacrificesValid) return DEFAULT_PREFERENCES.tableSort
  return {
    leaderboard: {
      orderBy: lb.orderBy as LeaderboardOrderBy,
      orderDirection: lb.orderDirection as SortDirection,
    },
    sacrifices: {
      orderBy: sac.orderBy as SacrificeOrderBy,
      orderDirection: sac.orderDirection as SortDirection,
    },
  }
}

/**
 * Merge a raw localStorage string into a complete Preferences object. Absent or
 * corrupt input yields DEFAULT_PREFERENCES; unknown keys are dropped; a key with the
 * wrong type falls back to its default. Pure — performs no localStorage access.
 */
export const parsePreferences = (raw: string | null): Preferences => {
  if (!raw) return DEFAULT_PREFERENCES
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_PREFERENCES
    return {
      disableImages:
        typeof parsed.disableImages === 'boolean'
          ? parsed.disableImages
          : DEFAULT_PREFERENCES.disableImages,
      showTestCampaigns:
        typeof parsed.showTestCampaigns === 'boolean'
          ? parsed.showTestCampaigns
          : DEFAULT_PREFERENCES.showTestCampaigns,
      tableSort: parseTableSort(parsed.tableSort),
    }
  } catch {
    return DEFAULT_PREFERENCES
  }
}

const loadPreferences = (): Preferences => {
  try {
    return parsePreferences(localStorage.getItem(PREFERENCES_STORAGE_KEY))
  } catch {
    return DEFAULT_PREFERENCES
  }
}

const savePreferences = (preferences: Preferences): void => {
  try {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences))
  } catch (error) {
    console.warn('Failed to save preferences to localStorage:', error)
  }
}

export interface PreferencesContextValue extends Preferences {
  setDisableImages: (value: boolean) => void
  setShowTestCampaigns: (value: boolean) => void
  setTableSort: (
    table: keyof TableSortPreference,
    sort: { orderBy: string; orderDirection: SortDirection }
  ) => void
}

// Default value supplied so usePreferences works without a provider (cosmetic
// cross-cutting concern; leaf components degrade to defaults).
const PreferencesContext = createContext<PreferencesContextValue>({
  ...DEFAULT_PREFERENCES,
  setDisableImages: () => {},
  setShowTestCampaigns: () => {},
  setTableSort: () => {},
})

export const PreferencesProvider = ({ children }: { children: React.ReactNode }) => {
  const [preferences, setPreferences] = useState<Preferences>(loadPreferences)

  // Persist whenever preferences change. `preferences` is the source of truth (not a
  // derived value), so the side effect belongs in an effect — not inside the state
  // updater, which must stay pure (StrictMode double-invokes updaters in development).
  useEffect(() => {
    savePreferences(preferences)
  }, [preferences])

  const setDisableImages = useCallback(
    (value: boolean) => setPreferences(previous => ({ ...previous, disableImages: value })),
    []
  )
  const setShowTestCampaigns = useCallback(
    (value: boolean) => setPreferences(previous => ({ ...previous, showTestCampaigns: value })),
    []
  )
  const setTableSort = useCallback(
    (table: keyof TableSortPreference, sort: { orderBy: string; orderDirection: SortDirection }) =>
      setPreferences(previous => ({
        ...previous,
        tableSort: { ...previous.tableSort, [table]: sort },
      })),
    []
  )

  const value = useMemo<PreferencesContextValue>(
    () => ({ ...preferences, setDisableImages, setShowTestCampaigns, setTableSort }),
    [preferences, setDisableImages, setShowTestCampaigns, setTableSort]
  )

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
}

export const usePreferences = (): PreferencesContextValue => useContext(PreferencesContext)

// Exported for tests that need to inject a value without a real provider/localStorage.
export { PreferencesContext }
